/**
 * Scenario builder for the cementing status API, used by the dev mock
 * interceptor and by `scripts/export-cementing-mocks.js` (which writes the
 * JSON fixtures under `mocks/cementing-status/`). Pure TypeScript: no Angular.
 *
 * Numbers follow the "LMS Cementing Mockup" final boards (10 questions a quiz,
 * L20 out of 20, threshold 14 = 70%, mastery 18 = 90%, 5 quizzes in the module
 * and 10 authored templates), so each scenario can be matched against a board.
 */
import {
  CementingAttempt, CementingCategory, CementingChoice, CementingDifficulty, CementingLevel, CementingModuleState,
  CementingMoment, CementingMomentKind, CementingStateReason, CementingStatus, CementingTarget
} from '../models/cementing-status';

interface AttemptSpec {
  n: number;
  /** Correct answers out of 10; 'A' abandoned, 'P' in progress. */
  score: number | 'A' | 'P';
  date: string;
  moment?: CementingMomentKind;
  choice?: CementingChoice;
  how?: 'PRESSED' | 'INFERRED';
  practice?: boolean;
  retake?: { score: number; date: string };
}

interface ModuleSpec {
  difficulty: CementingDifficulty;
  attempts: AttemptSpec[];
  state?: CementingModuleState;
  stateReason?: CementingStateReason;
  stateQuizNumber?: number;
  /** Real quiz number of the next check-in (open modules). */
  nextCheckin?: number;
  nextCheckinIsFinal?: boolean;
  /** Overrides the next quiz: null = none can be started. */
  nextQuiz?: number | null;
  legacy?: boolean;
  thresholdScore?: number;
  masteryScore?: number;
}

const Q = 10;
const L20_MAX = 20;
const TEMPLATES = 10;
const IN_MODULE = 5;
const THRESHOLD_PCT = 70;
const MAX_GAP = 20;

function bar(score: number) {
  const allowed = Math.max(0, L20_MAX - score);
  return { score, percent: Math.round(score * 100 / L20_MAX), mistakesAllowed: allowed, perQuizMistakes: Math.floor(allowed / 2) };
}

function above(score: number): boolean {
  return score * 100 >= THRESHOLD_PCT * Q;
}

function pairEven(a: number, b: number): boolean {
  return (above(a) && above(b)) || Math.abs(a - b) * 100 <= MAX_GAP * Q;
}

function targetScore(prev: number, barScore: number): number | null {
  for (let s = 0; s <= Q; s++) {
    if (prev + s >= barScore && pairEven(prev, s)) { return s; }
  }
  return null;
}

function levelOf(l20: number, threshold: number, mastery: number): CementingLevel {
  return l20 >= mastery ? 'MASTERY_MET' : l20 >= threshold ? 'THRESHOLD_MET' : 'BELOW_THRESHOLD';
}

function iso(date: string, hour: number): string {
  return date + 'T' + (hour < 10 ? '0' : '') + hour + ':00:00.000+0000';
}

function uuid(seed: string): string {
  // Deterministic, readable ids so the fixtures diff cleanly.
  let h = 0;
  for (let i = 0; i < seed.length; i++) { h = (h * 31 + seed.charCodeAt(i)) >>> 0; }
  const hex = ('00000000' + h.toString(16)).slice(-8);
  return hex + '-' + hex.slice(0, 4) + '-4' + hex.slice(1, 4) + '-8' + hex.slice(2, 5) + '-' + hex + hex.slice(0, 4);
}

function breakdown(mistakes: number): { [bucket: string]: number } {
  const b: { [bucket: string]: number } = { ABILITY: 0, SPEED: 0, JUDGEMENT: 0, SCORE_BOOSTER: 0, CASCADE: 0, MASTERY: Q - mistakes, NOT_REACHED: 0 };
  const order = ['JUDGEMENT', 'SPEED', 'ABILITY', 'JUDGEMENT', 'CASCADE', 'SPEED', 'JUDGEMENT'];
  for (let i = 0; i < mistakes; i++) { b[order[i % order.length]]++; }
  return b;
}

export function buildModule(subject: string, spec: ModuleSpec): CementingCategory {
  const threshold = spec.thresholdScore === undefined ? 14 : spec.thresholdScore;
  const mastery = spec.masteryScore === undefined ? 18 : spec.masteryScore;
  const key = subject + '_' + spec.difficulty;
  const fixed = !spec.legacy;
  const templateId = (n: number) => fixed ? uuid(key + '-T' + n) : null;

  // ---- quiz slots
  const slots: CementingAttempt[] = [];
  const total = fixed ? TEMPLATES : Math.max(IN_MODULE, spec.attempts.length);
  for (let n = 1; n <= total; n++) {
    slots.push({
      quizNumber: n, correlationId: templateId(n), status: null, quizId: null, kind: 'NOT_STARTED', score: null, questions: null,
      mistakes: null, accuracyPercent: null, startedAt: null, finishedAt: null, timing: null,
      recommendedTiming: n === 1 ? 'RELAXED' : 'STANDARD', endMode: null, retakes: []
    });
  }
  const moments: CementingMoment[] = [];
  let counted = 0;
  let abandoned = 0;
  let prev = null as { n: number; score: number } | null;
  let warmup = null as number | null;
  let latest = null as { score: number; prev: { n: number; score: number }; cur: { n: number; score: number } } | null;
  let checkins = 0;
  let inProgress = null as AttemptSpec | null;
  let waiting = null as number | null;
  let bestLevel = 'BELOW_THRESHOLD' as CementingLevel;
  spec.attempts.forEach((a, i) => {
    const slot = slots[a.n - 1];
    const quizId = 910000 + a.n;
    slot.quizId = quizId;
    slot.startedAt = iso(a.date, 10);
    slot.timing = a.n === 1 ? 'RELAXED' : 'STANDARD';
    if (a.score === 'P') {
      slot.status = 'INPROGRESS';
      slot.kind = 'IN_PROGRESS';
      inProgress = a;
      return;
    }
    if (a.score === 'A') {
      slot.status = 'ABANDONED';
      slot.kind = 'ABANDONED';
      slot.endMode = 'ABANDONED';
      slot.finishedAt = iso(a.date, 11);
      abandoned++;
      moments.push(moment(a, null, prev, null, 'ABANDONED', false));
      return;
    }
    slot.status = 'COMPLETED';
    slot.score = a.score;
    slot.questions = Q;
    slot.mistakes = Q - a.score;
    slot.accuracyPercent = Math.round(a.score * 100 / Q);
    slot.finishedAt = iso(a.date, 11);
    slot.endMode = 'FINISHED';
    if (a.retake) {
      slot.retakes.push({
        quizNumber: a.n, correlationId: templateId(a.n), status: 'COMPLETED', quizId: quizId + 500, kind: 'RETAKE', score: a.retake.score, questions: Q,
        mistakes: Q - a.retake.score, accuracyPercent: Math.round(a.retake.score * 100 / Q), startedAt: iso(a.retake.date, 11), finishedAt: iso(a.retake.date, 12),
        timing: 'STANDARD', recommendedTiming: 'STANDARD', endMode: 'FINISHED', retakes: []
      });
    }
    if (a.practice) {
      slot.kind = 'PRACTICE';
      const l20 = prev ? prev.score + a.score : null;
      moments.push(moment(a, null, prev, l20, 'PRACTICE', false));
      if (prev && l20 !== null) { latest = { score: l20, prev, cur: { n: a.n, score: a.score } }; }
      prev = { n: a.n, score: a.score };
      return;
    }
    counted++;
    const kind: CementingMomentKind = a.moment || (counted === 1 ? 'WARMUP_DONE' : counted === 2 ? 'FIRST_CHECKIN_NEXT' : 'SILENT');
    slot.kind = counted === 1 ? 'WARMUP' : 'COUNTED';
    if (counted === 1) { warmup = a.score; }
    const l20 = counted >= 3 && prev ? prev.score + a.score : null;
    const m = moment(a, counted, prev, l20, kind, true);
    if (l20 !== null) {
      const level = levelOf(l20, threshold, mastery);
      const even = prev ? pairEven(prev.score, a.score) : true;
      if (!even && level !== 'BELOW_THRESHOLD' && level !== bestLevel) { m.uneven = true; m.unevenWouldBe = level; }
      if (even && (level === 'MASTERY_MET' || (level === 'THRESHOLD_MET' && bestLevel === 'BELOW_THRESHOLD'))) {
        if (level !== bestLevel) { m.newLevel = level; }
        bestLevel = level;
      }
      latest = { score: l20, prev, cur: { n: a.n, score: a.score } };
    }
    if (['CHECKIN_1', 'CHECKIN_BELOW_AGAIN', 'CHECKIN_MASTERY'].indexOf(kind) >= 0) { checkins++; }
    // Waiting: no choice yet, or a pressed "keep going" still changeable (no quiz since), as the server reports it.
    const changeable = m.choice === 'KEEP_GOING' && m.choiceHow === 'PRESSED';
    if (m.takesChoice && (!m.choice || changeable) && i === spec.attempts.length - 1) { waiting = a.n; m.active = true; }
    moments.push(m);
    prev = { n: a.n, score: a.score };
  });

  function moment(a: AttemptSpec, position: number | null, before: { n: number; score: number } | null, l20: number | null,
                  kind: CementingMomentKind, counts: boolean): CementingMoment {
    const takesChoice = kind === 'CELEBRATE_THRESHOLD' || kind === 'CHECKIN_1' || kind === 'CHECKIN_BELOW_AGAIN' || kind === 'CHECKIN_MASTERY';
    const allowsStop = kind === 'CELEBRATE_THRESHOLD' || kind === 'CHECKIN_BELOW_AGAIN' || kind === 'CHECKIN_MASTERY';
    const score = typeof a.score === 'number' ? a.score : null;
    return {
      quizId: 910000 + a.n, quizNumber: a.n, countedPosition: position, score,
      prevQuizNumber: before ? before.n : null, prevScore: before ? before.score : null,
      l20, l20Max: L20_MAX, mistakes: l20 === null ? null : L20_MAX - l20,
      moment: kind, takesChoice, allowsStop, active: false,
      uneven: false, unevenWouldBe: null,
      pairEven: l20 === null || !before || score === null ? null : pairEven(before.score, score),
      relaxedInPair: counts && before ? (a.n === 1 || before.n === 1) : null,
      newLevel: null, statusAfter: null, farBelowQuizNumber: null,
      choice: a.choice || null, choiceHow: a.choice ? (a.how || 'PRESSED') : null,
      choiceAt: a.choice ? iso(a.date, 12) : null, createdAt: iso(a.date, 11)
    };
  }

  // ---- state
  let state: CementingModuleState = spec.state || (counted === 0 && abandoned === 0 && !inProgress ? 'NOT_STARTED'
    : bestLevel === 'BELOW_THRESHOLD' ? 'IN_PROGRESS' : 'THRESHOLD_MET_GOING_FOR_MASTERY');
  if (spec.state === undefined && bestLevel === 'MASTERY_MET') { state = 'DONE_MASTERY_MET'; }
  const open = state === 'NOT_STARTED' || state === 'IN_PROGRESS' || state === 'THRESHOLD_MET_GOING_FOR_MASTERY';
  // Quizzes taken after the module closed are practice (the boards show practice after any Done state).
  const practice = !open;
  const canTake = open || state === 'DONE_MASTERY_MET' || state === 'NOT_NEEDED' || spec.nextQuiz !== undefined;
  const countsAsComplete = !open;
  if (!open) { waiting = null; }
  moments.forEach(m => { m.statusAfter = m.quizNumber === spec.stateQuizNumber ? state : (open ? state : 'IN_PROGRESS'); });
  if (!open && spec.stateQuizNumber) {
    const closing = moments.filter(m => m.quizNumber === spec.stateQuizNumber).pop();
    if (closing) { closing.statusAfter = state; }
  }

  // ---- next quiz
  const used = spec.attempts.filter(a => a.score !== 'P').map(a => a.n);
  const unused = slots.map(s => s.quizNumber).filter(n => used.indexOf(n) < 0 && !(inProgress && inProgress.n === n));
  const quizzesLeft = Math.max(0, total - used.length - (inProgress ? 1 : 0));
  let nextNumber: number | null = spec.nextQuiz !== undefined ? spec.nextQuiz : (canTake && unused.length ? unused[0] : null);
  if (inProgress) { nextNumber = inProgress.n; }
  const nextQuiz = nextNumber === null ? null : {
    quizNumber: nextNumber, correlationId: templateId(nextNumber),
    maxTimeSeconds: spec.difficulty === 'HARD' ? 1500 : 1350, relaxedMaxTimeSeconds: 1800,
    recommendedTiming: nextNumber === 1 ? 'RELAXED' as const : 'STANDARD' as const,
    practice, inProgressQuizId: inProgress ? 910000 + inProgress.n : null,
    resumable: !!inProgress, resumeUntil: inProgress ? iso(inProgress.date, 12) : null, answeredQuestions: inProgress ? 4 : null
  };

  // ---- target
  let target: CementingTarget | null = null;
  if (open && counted > 0 && nextNumber !== null && !inProgress) {
    const toThreshold = bestLevel === 'BELOW_THRESHOLD';
    const barScore = toThreshold ? threshold : mastery;
    const perQuiz = Math.floor(Math.max(0, L20_MAX - barScore) / 2);
    if (counted === 1 || !prev) {
      target = { kind: 'AFTER_WARMUP', quizNumber: nextNumber, bar: toThreshold ? 'THRESHOLD' : 'MASTERY', barScore, prevScore: null, targetScore: null, mistakesOrFewer: null, reachable: false, perQuizMistakes: perQuiz };
    } else {
      const t = targetScore(prev.score, barScore);
      target = {
        kind: t === null ? 'OUT_OF_REACH' : 'PAIR', quizNumber: nextNumber, bar: toThreshold ? 'THRESHOLD' : 'MASTERY', barScore,
        prevScore: prev.score, targetScore: t, mistakesOrFewer: t === null ? null : Q - t, reachable: t !== null, perQuizMistakes: perQuiz
      };
    }
  }

  const latestL20 = latest ? {
    score: latest.score, l20Max: L20_MAX, percent: Math.round(latest.score * 100 / L20_MAX), mistakes: L20_MAX - latest.score,
    pairEven: pairEven(latest.prev.score, latest.cur.score), prevQuizNumber: latest.prev.n, prevScore: latest.prev.score,
    quizNumber: latest.cur.n, quizScore: latest.cur.score, farBelowQuizNumber: null
  } : null;

  // Every counted quiz but the warm-up (any of them can be half of an L20); the total is the latest pair's.
  const byQuiz = latest ? slots.filter(s => s.status === 'COMPLETED' && s.kind !== 'WARMUP' && s.score !== null)
    .map(s => ({ quizId: 910000 + s.quizNumber, quizNumber: s.quizNumber, buckets: breakdown(Q - s.score) })) : [];
  const totalBreakdown: { [bucket: string]: number } = {};
  byQuiz.filter(b => latest && (b.quizNumber === latest.prev.n || b.quizNumber === latest.cur.n))
    .forEach(b => Object.keys(b.buckets).forEach(k => totalBreakdown[k] = (totalBreakdown[k] || 0) + b.buckets[k]));

  return {
    categoryKey: key,
    correlationId: uuid(key),
    difficulty: spec.difficulty,
    name: subject + ' ' + (spec.difficulty === 'HARD' ? 'Hard' : 'Medium') + ' cementing',
    sequence: spec.difficulty === 'MEDIUM' ? 1 : 2,
    rules: {
      questionsPerQuiz: Q, l20Max: L20_MAX, quizzesInModule: IN_MODULE, threshold: bar(threshold), masteryTarget: bar(mastery),
      thresholdPct: THRESHOLD_PCT, maxGapPoints: MAX_GAP, relaxedRecommendedQuiz: 1,
      hardWarmupPct: spec.difficulty === 'HARD' ? 60 : null
    },
    summary: {
      status: state, stateReason: open ? null : (spec.stateReason || 'CHOSEN'), stateQuizNumber: open ? null : (spec.stateQuizNumber || null),
      stateQuizId: open || !spec.stateQuizNumber ? null : 910000 + spec.stateQuizNumber,
      open, bestLevel, countsAsComplete, canTakeQuiz: canTake, practice,
      countedQuizzes: counted, abandonedQuizzes: abandoned, quizzesLeft, quizzesTotal: total, checkinsSoFar: checkins,
      warmupScore: warmup, latestL20,
      nextCheckinQuizNumber: open ? (spec.nextCheckin || null) : null, nextCheckinIsFinal: !!spec.nextCheckinIsFinal,
      nextQuizTarget: target, waitingQuizNumber: waiting
    },
    nextQuiz,
    moments,
    quizzes: slots,
    l20MistakeBreakdown: totalBreakdown,
    l20MistakeBreakdownByQuiz: byQuiz
  };
}

/* ---------------------------------------------------------------- scenarios */

const hardNew = (): ModuleSpec => ({ difficulty: 'HARD', attempts: [], thresholdScore: 11, masteryScore: 17 });

// The storyline of the final boards: Q1 60 (warm-up), Q2 60, Q3 70 (check-in, not there), Q4 70 (threshold met),
// Q5 80, Q6 90 (mastery check-in), Q7 80, Q8 100 (mastery).
const story: AttemptSpec[] = [
  { n: 1, score: 6, date: '2026-10-01' },
  { n: 2, score: 6, date: '2026-10-02' },
  { n: 3, score: 7, date: '2026-10-02', moment: 'CHECKIN_1' },
  { n: 4, score: 7, date: '2026-10-05', moment: 'CELEBRATE_THRESHOLD' },
  { n: 5, score: 8, date: '2026-10-06' },
  { n: 6, score: 9, date: '2026-10-07', moment: 'CHECKIN_MASTERY' },
  { n: 7, score: 8, date: '2026-10-09' },
  { n: 8, score: 10, date: '2026-10-10', moment: 'CELEBRATE_MASTERY' }
];
const kept = (a: AttemptSpec, choice: CementingChoice = 'KEEP_GOING', how: 'PRESSED' | 'INFERRED' = 'INFERRED'): AttemptSpec => ({ ...a, choice, how });

export function buildScenario(scenario: string, subject: string): CementingStatus | null {
  const fixed = (medium: ModuleSpec, hard: ModuleSpec = hardNew()): CementingStatus =>
    ({ subject, mode: 'FIXED', categories: [buildModule(subject, medium), buildModule(subject, hard)] });
  const medium = (attempts: AttemptSpec[], rest: Partial<ModuleSpec> = {}): ModuleSpec => ({ difficulty: 'MEDIUM', attempts, ...rest });

  switch (scenario) {
    case 'none':                 // subject without cementing
      return { subject, mode: null, categories: [] };
    case 'not-started':          // nothing attempted yet
      return fixed(medium([], { nextCheckin: 3 }));
    case 'after-q1':             // board 1: warm-up done
      return fixed(medium(story.slice(0, 1), { nextCheckin: 3 }));
    case 'after-q2':             // board 2
      return fixed(medium(story.slice(0, 2), { nextCheckin: 3 }));
    case 'checkin-q3':           // board 3: first check-in, not there yet (choice inferred by starting Q4)
      return fixed(medium(story.slice(0, 3), { nextCheckin: 5 }));
    case 'threshold-q4':         // board 4: threshold met, Go for mastery? (boards 5-8 follow from the buttons)
      return fixed(medium([...story.slice(0, 2), kept(story[2]), story[3]], { nextCheckin: 6 }));
    case 'done-threshold-q4':    // board 7: finished at Threshold met after Q4
      return fixed(medium([...story.slice(0, 2), kept(story[2]), kept(story[3], 'STOP', 'PRESSED')],
        { state: 'DONE_THRESHOLD_MET', stateReason: 'CHOSEN', stateQuizNumber: 4, nextQuiz: null }));
    case 'after-q5':             // board 9
      return fixed(medium([...story.slice(0, 2), kept(story[2]), kept(story[3]), story[4]], { nextCheckin: 6 }));
    case 'checkin-mastery-q6':   // board 10: mastery check-in, choose (boards 11-14 follow from the buttons)
      return fixed(medium([...story.slice(0, 2), kept(story[2]), kept(story[3]), story[4], story[5]], { nextCheckin: 8 }));
    case 'with-retake':          // Table board: after Q6 with a retake of Q2
      return fixed(medium([story[0], { ...story[1], retake: { score: 9, date: '2026-10-02' } }, kept(story[2]), kept(story[3]), story[4], story[5]], { nextCheckin: 8 }));
    case 'done-threshold-q6':    // board 13: finished at Threshold met after Q6
      return fixed(medium([...story.slice(0, 2), kept(story[2]), kept(story[3]), story[4], kept(story[5], 'STOP', 'PRESSED')],
        { state: 'DONE_THRESHOLD_MET', stateReason: 'CHOSEN', stateQuizNumber: 6, nextQuiz: null }));
    case 'done-then-practice':   // board 15: done after Q6, then a practice quiz Q7 (next Q8 is practice)
      return fixed(medium([...story.slice(0, 2), kept(story[2]), kept(story[3]), story[4], kept(story[5], 'STOP', 'PRESSED'), { ...story[6], practice: true }],
        { state: 'DONE_THRESHOLD_MET', stateReason: 'CHOSEN', stateQuizNumber: 6, nextQuiz: 8 }));
    case 'checkin-below-q5':     // board 16: later check-in below the threshold, choose (boards 17-20 follow)
      return fixed(medium([
        { n: 1, score: 6, date: '2026-10-01' }, { n: 2, score: 6, date: '2026-10-02' },
        kept({ n: 3, score: 6, date: '2026-10-02', moment: 'CHECKIN_1' }), { n: 4, score: 6, date: '2026-10-03' },
        { n: 5, score: 6, date: '2026-10-05', moment: 'CHECKIN_BELOW_AGAIN' }
      ], { nextCheckin: 7 }));
    case 'done-not-met-q5':      // board 19: finished below the threshold
      return fixed(medium([
        { n: 1, score: 6, date: '2026-10-01' }, { n: 2, score: 6, date: '2026-10-02' },
        kept({ n: 3, score: 6, date: '2026-10-02', moment: 'CHECKIN_1' }), { n: 4, score: 6, date: '2026-10-03' },
        kept({ n: 5, score: 6, date: '2026-10-05', moment: 'CHECKIN_BELOW_AGAIN' }, 'STOP', 'PRESSED')
      ], { state: 'DONE_THRESHOLD_NOT_MET', stateReason: 'CHOSEN', stateQuizNumber: 5, nextQuiz: null }));
    case 'mastery-q8':           // board 21: mastery reached after Q8, practice open
      return fixed(medium([...story.slice(0, 2), kept(story[2]), kept(story[3]), story[4], kept(story[5]), story[6], story[7]],
        { state: 'DONE_MASTERY_MET', stateReason: 'MASTERY_REACHED', stateQuizNumber: 8 }));
    case 'all-used-met':         // board 22: all 10 quizzes used, threshold met
      return fixed(medium([
        { n: 1, score: 6, date: '2026-10-01' }, { n: 2, score: 6, date: '2026-10-02' },
        kept({ n: 3, score: 6, date: '2026-10-03', moment: 'CHECKIN_1' }), { n: 4, score: 7, date: '2026-10-04' },
        kept({ n: 5, score: 6, date: '2026-10-05', moment: 'CHECKIN_BELOW_AGAIN' }, 'KEEP_GOING', 'PRESSED'), { n: 6, score: 7, date: '2026-10-06' },
        kept({ n: 7, score: 7, date: '2026-10-07', moment: 'CELEBRATE_THRESHOLD' }, 'KEEP_GOING', 'PRESSED'), { n: 8, score: 8, date: '2026-10-08' },
        kept({ n: 9, score: 7, date: '2026-10-09', moment: 'CHECKIN_MASTERY' }, 'KEEP_GOING', 'PRESSED'), { n: 10, score: 8, date: '2026-10-10', moment: 'FINAL' }
      ], { state: 'DONE_THRESHOLD_MET', stateReason: 'QUIZZES_USED_UP', stateQuizNumber: 10, nextQuiz: null }));
    case 'all-used-not-met':     // board 23: all 10 quizzes used, threshold not met
      return fixed(medium([
        { n: 1, score: 5, date: '2026-10-01' }, { n: 2, score: 6, date: '2026-10-02' },
        kept({ n: 3, score: 6, date: '2026-10-03', moment: 'CHECKIN_1' }), { n: 4, score: 6, date: '2026-10-04' },
        kept({ n: 5, score: 5, date: '2026-10-05', moment: 'CHECKIN_BELOW_AGAIN' }, 'KEEP_GOING', 'PRESSED'), { n: 6, score: 6, date: '2026-10-06' },
        kept({ n: 7, score: 6, date: '2026-10-07', moment: 'CHECKIN_BELOW_AGAIN' }, 'KEEP_GOING', 'PRESSED'), { n: 8, score: 6, date: '2026-10-08' },
        kept({ n: 9, score: 6, date: '2026-10-09', moment: 'CHECKIN_BELOW_AGAIN' }, 'KEEP_GOING', 'PRESSED'), { n: 10, score: 6, date: '2026-10-10', moment: 'FINAL' }
      ], { state: 'DONE_THRESHOLD_NOT_MET', stateReason: 'QUIZZES_USED_UP', stateQuizNumber: 10, nextQuiz: null }));
    case 'uneven-q3':            // check-in 1 with an uneven pair: Q2 40%, Q3 100% add up to the threshold but swing too much
      return fixed(medium([story[0], { n: 2, score: 4, date: '2026-10-02' }, { n: 3, score: 10, date: '2026-10-03', moment: 'CHECKIN_1' }], { nextCheckin: 5 }));
    case 'after-q2-low':         // Q2 at 40%: no Q3 score makes an even pair at the threshold
      return fixed(medium([story[0], { n: 2, score: 4, date: '2026-10-02' }], { nextCheckin: 3 }));
    case 'in-progress':          // Q2 started but not finished: Resume
      return fixed(medium([story[0], { n: 2, score: 'P', date: '2026-10-02' }], { nextCheckin: 3 }));
    case 'abandoned':            // Q2 abandoned: it doesn't count, Q3 is next
      return fixed(medium([story[0], { n: 2, score: 'A', date: '2026-10-02' }], { nextCheckin: 4 }));
    case 'hard-warning':         // Medium below the threshold and a weak Hard warm-up
      return fixed(medium(story.slice(0, 3), { nextCheckin: 5 }),
        { difficulty: 'HARD', attempts: [{ n: 1, score: 5, date: '2026-10-04' }], nextCheckin: 3, thresholdScore: 11, masteryScore: 17 });
    case 'legacy':               // dynamic cementing subject: same shape, no template ids
      return { subject, mode: 'LEGACY', categories: [
        buildModule(subject, medium([story[0], story[1]], { nextCheckin: 3, legacy: true })),
        buildModule(subject, { ...hardNew(), legacy: true })
      ] };
    default:
      return fixed(medium([...story.slice(0, 2), kept(story[2]), story[3]], { nextCheckin: 6 }));
  }
}

/** Every scenario name buildScenario understands, in review order. */
export const CEMENTING_MOCK_SCENARIOS = [
  'not-started', 'after-q1', 'after-q2', 'checkin-q3', 'threshold-q4', 'done-threshold-q4', 'after-q5', 'checkin-mastery-q6',
  'with-retake', 'done-threshold-q6', 'done-then-practice', 'checkin-below-q5', 'done-not-met-q5', 'mastery-q8', 'all-used-met',
  'all-used-not-met', 'uneven-q3', 'after-q2-low', 'in-progress', 'abandoned', 'hard-warning', 'legacy', 'none'
];
